export class Lightbox {
    constructor(data) {
        if (!this.set(data)) return;
        this.init();
    }

    set(data) {
        if (!data) return false;
        const { el, lightboxModalClass } = data;

        this.lightboxModalClass = lightboxModalClass || 'lightbox-modal';

        this.root = el.querySelector('.lightbox');
        if (!this.root) return;
        this.items = this.root.querySelectorAll('[data-lightbox-url]');
        if (this.items.length === 0) return;
        this.itemsData = [];
        this.lightboxItems = [];

        this.activeItemKey = null;
        this.lightboxEl = null;
        this.lightboxBackgroundEl = null;
        this.lightboxCloseButton = null;
        this.lightboxArrows = [];
        this.activeCounterItemEl = null;
        this.previousFocusedElement = null;

        this.boundHandleEscapeClick = null;
        this.boundHandleArrowClick = null;

        this.touchstartX = 0;
        this.touchendX = 0;
        this.desktopTouchX = 0;

        return true;
    }

    init() {
        this.setItemsClick();
    }

    setItemsClick() {
        this.items.forEach((item, key) => {
            item.setAttribute("tabindex", 0);
            item.addEventListener('click', e => {
                e.preventDefault();
                this.activeItemKey = key;
                this.handleItemClick();
            });
            item.addEventListener('keydown', e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.activeItemKey = key;
                    this.handleItemClick();
                }
            });
        });
    }

    handleItemClick() {
        if (this.itemsData.length === 0) this.setItemsData();
        this.blockItems();
        this.createLightbox();
        this.setActiveItem();

        if (this.itemsData.length > 1) this.initTouch();

        this.handleEscapeClickListener();
        if (this.lightboxBackgroundEl) this.handleBackgroundClick();
        if (this.lightboxCloseButton) this.handleCloseClick();
        if (this.lightboxArrows.length > 0) {
            this.handleArrowListener();
            this.handleKeyboardArrowListener();
        }

        setTimeout(() => {
            this.showLightbox();
        }, 100);
    }

    setItemsData() {
        this.items.forEach((item, key) => {
            const imgElement = item.querySelector('img');
            const wordpressImgId = parseInt(
                item.getAttribute('data-lightbox-image-id')
            );
            this.itemsData.push({
                key: key,
                id: wordpressImgId,
                url: item.getAttribute('data-lightbox-url'),
                alt: imgElement.alt,
            });
        });
    }

    createLightbox() {
        if (this.itemsData.length === 0) return;

        // lightbox modal
        this.lightboxEl = document.createElement('div');
        this.lightboxEl.setAttribute("tabindex", 0);
        this.lightboxEl.classList.add(`${this.lightboxModalClass}`);

        // dark background
        this.lightboxBackgroundEl = document.createElement('div');
        this.lightboxBackgroundEl.classList.add(
            `${this.lightboxModalClass}__background`
        );

        // wrapper
        const wrapper = document.createElement('div');
        wrapper.classList.add(
            `${this.lightboxModalClass}__wrapper`,
            'p-relative'
        );

        // close button
        this.lightboxCloseButton = document.createElement('button');
        this.lightboxCloseButton.setAttribute("tabindex", 0);
        this.lightboxCloseButton.type = 'button';
        this.lightboxCloseButton.classList.add(
            `${this.lightboxModalClass}__close`,
            'icon',
            'icon-close'
        );
        wrapper.appendChild(this.lightboxCloseButton);

        // arrows
        if (this.itemsData.length > 1) {
            const prevArrow = document.createElement('button');
            prevArrow.classList.add(
                `${this.lightboxModalClass}__arrow`,
                '-prev',
                'icon',
                'icon-chevron-left'
            );
            prevArrow.type = 'button';
            prevArrow.setAttribute("tabindex", 0);
            this.lightboxArrows.push(prevArrow);
            wrapper.appendChild(prevArrow);

            const nextArrow = document.createElement('button');
            nextArrow.classList.add(
                `${this.lightboxModalClass}__arrow`,
                '-next',
                'icon',
                'icon-chevron-right'
            );
            nextArrow.type = 'button';
            nextArrow.setAttribute("tabindex", 0);
            this.lightboxArrows.push(nextArrow);
            wrapper.appendChild(nextArrow);
        }

        // counter
        const counter = document.createElement('div');
        counter.classList.add(`${this.lightboxModalClass}__counter`);
        this.activeCounterItemEl = document.createElement('span');
        this.activeCounterItemEl.classList.add(
            `${this.lightboxModalClass}__span`,
            '-active'
        );
        this.activeCounterItemEl.textContent = 1;
        const otherCounterText = document.createElement('span');
        otherCounterText.classList.add(
            `${this.lightboxModalClass}__span`,
            '-other'
        );
        otherCounterText.textContent = ` / ${this.itemsData.length}`;
        counter.appendChild(this.activeCounterItemEl);
        counter.appendChild(otherCounterText);
        wrapper.appendChild(counter);

        // slides
        this.itemsData.forEach(item => {
            const { key, id, url, alt } = item;
            const img = document.createElement('img');
            img.classList.add(`${this.lightboxModalClass}__image`);
            img.setAttribute('data-lightbox-item-key', key);
            img.setAttribute('data-lightbox-item-id', id);
            img.src = url;
            img.alt = alt;
            img.draggable = false;
            wrapper.appendChild(img);
            this.lightboxItems.push(img);
        });

        // append background and wrapper to lightbox modal
        this.lightboxEl.appendChild(this.lightboxBackgroundEl);
        this.lightboxEl.appendChild(wrapper);

        // append lightbox modal to body
        document.body.appendChild(this.lightboxEl);
    }

    setActiveItem(direction) {
        if (!this.lightboxItems) return;
        this.lightboxItems.forEach(item => {
            if (item.classList.contains('-active')) {
                item.classList.remove('-active');
                if (direction) {
                    item.classList.add(`-${direction}`);
                    setTimeout(() => {
                        item.classList.remove(`-${direction}`);
                    }, 300);
                }
            }
        });
        const activeEl = this.lightboxItems[this.activeItemKey];
        if (direction) {
            activeEl.classList.add(
                `-${direction === 'left' ? 'right' : 'left'}`
            );
            setTimeout(() => {
                activeEl.classList.remove(
                    `-${direction === 'left' ? 'right' : 'left'}`
                );
                activeEl.classList.add('-active');
            }, 300);
        } else {
            activeEl.classList.add('-active');
        }

        this.handleActiveCounterNumber();
    }

    handleActiveCounterNumber() {
        if (!this.activeCounterItemEl) return;

        this.activeCounterItemEl.textContent = this.activeItemKey + 1;
    }

    initTouch() {
        this.lightboxItems.forEach(item => {
            item.addEventListener('touchstart', e => {
                this.touchstartX = e.changedTouches[0].screenX;
            });

            item.addEventListener('touchend', e => {
                this.touchendX = e.changedTouches[0].screenX;
                this.handleSwipe(item);
            });

            item.addEventListener('mousedown', e => {
                this.touchstartX = e.pageX;
            });

            item.addEventListener('mouseup', e => {
                this.touchendX = e.pageX;
                this.handleSwipe(item);
            });
        });
    }

    handleSwipe(item) {
        if (this.touchendX > this.touchstartX) {
            this.activeItemKey =
                this.activeItemKey === 0
                    ? this.lightboxItems.length - 1
                    : this.activeItemKey - 1;
            this.setActiveItem('right');
        }
        if (this.touchendX < this.touchstartX) {
            this.activeItemKey =
                this.activeItemKey === this.lightboxItems.length - 1
                    ? 0
                    : this.activeItemKey + 1;
            this.setActiveItem('left');
        }
    }

    handleEscapeClickListener() {
        this.boundHandleEscapeClick = this.handleEscapeClick.bind(this);
        document.addEventListener('keydown', this.boundHandleEscapeClick);
    }

    handleEscapeClick(e) {
        if (e.key !== 'Escape') return;
        this.closeLightbox();
    }

    handleBackgroundClick() {
        this.lightboxBackgroundEl.addEventListener(
            'click',
            this.closeLightbox.bind(this)
        );
    }

    handleCloseClick() {
        this.lightboxCloseButton.addEventListener(
            'click',
            this.closeLightbox.bind(this)
        );
    }

    handleArrowListener() {
        this.lightboxArrows.forEach(arrow => {
            arrow.addEventListener('click', () => this.handleArrowClick(arrow));
        });
    }

    handleKeyboardArrowListener() {
        this.boundHandleArrowClick = this.handleArrowClick.bind(this);
        document.addEventListener('keydown', this.boundHandleArrowClick);
    }

    handleArrowClick(arrow) {
        if (
            arrow?.key === 'ArrowLeft' ||
            (!arrow.key && arrow.classList.contains('-prev'))
        ) {
            this.activeItemKey =
                this.activeItemKey === 0
                    ? this.lightboxItems.length - 1
                    : this.activeItemKey - 1;
            this.setActiveItem('right');
        } else if (
            arrow?.key === 'ArrowRight' ||
            (!arrow.key && arrow.classList.contains('-next'))
        ) {
            this.activeItemKey =
                this.activeItemKey === this.lightboxItems.length - 1
                    ? 0
                    : this.activeItemKey + 1;
            this.setActiveItem('left');
        }
    }

    closeLightbox() {
        this.hideLightbox();
        setTimeout(() => {
            this.removeLightbox();
            this.unlockItems();
        }, 300);
    }

    showLightbox() {
        this.lightboxEl.classList.add('-show');
        this.previousFocusedElement = document.activeElement;
        setTimeout(() => {
            this.lightboxEl.focus();
        }, 300);
    }

    hideLightbox() {
        this.lightboxEl.classList.remove('-show');
        this.previousFocusedElement.focus();
    }

    removeLightbox() {
        if (this.lightboxEl === null) return;
        this.removeListeners();
        document.body.removeChild(this.lightboxEl);
        this.lightboxEl = null;
        this.lightboxItems = [];
        this.lightboxArrows = [];
        this.activeItemKey = null;
        this.lightboxBackgroundEl = null;
        this.lightboxCloseButton = null;
        this.activeCounterItemEl = null;
    }

    removeListeners() {
        document.removeEventListener('keydown', this.boundHandleEscapeClick);
        document.removeEventListener('keydown', this.boundHandleArrowClick);

        this.boundHandleEscapeClick = null;
        this.boundHandleArrowClick = null;
    }

    totalDestroy() {
        this.removeListeners();
    }

    blockItems() {
        this.items.forEach(item => item.classList.add('-block'));
    }

    unlockItems() {
        this.items.forEach(item => item.classList.remove('-block'));
    }
}
